import React, { ChangeEvent, useCallback } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import { OptionBase } from '../../../../../../../components/Select/types';
import { ProjectWithStatus } from '../../../../Projects/types';
import { TimeEntryData } from './types';
import { minToHoursInput } from './utils';
import { Theme } from '../../../../../../../theme';
import styled from '@emotion/styled';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import { FieldType } from '../../../../../__generated__/graphql';
import { formatHoursToHHMM } from '../../../../../../../utils/time';
import { formatTime24h } from '../../../../../../../utils/dateUtils';
import {
  ExtraFieldsAreaSimple,
  ExtraFieldsSupportedObject,
} from '../../../../../components/ExtraFields';
import {
  extraFieldsContextParams,
  ExtraFieldsFormId,
} from '../../../../../components/ExtraFields/types';
import { useUserInfo } from '../../../../../context/UserInfo';
import { ExtraFieldInternalValue } from '../../../../../components/ExtraFields/extraFieldInternalValue';
import {
  SelectSingleBase,
  RenderOptionParams,
} from '../../../../../components/Selects/SelectSingleBase';
import { TimeEntryProps } from '../../../../Projects/types';

type Props = {
  day: string;
  timeTracked: TimeEntryData;
  projects: ProjectWithStatus[];
  disabled: boolean;
  onDataChange: (timeTracked: TimeEntryData) => void;
  onDelete: (id: string) => void;
  compactMode: boolean;
  showTimeEntryColumn: boolean;
  timeEntries: TimeEntryProps[];
  className?: string;
};

const StyledSelect = styled(Form.Select)((props) =>
  props.disabled
    ? {
        backgroundColor: `${Theme.color.white} !important`,
      }
    : {},
);

const ProjectDayTimeEntry: React.FC<Props> = ({
  day,
  timeTracked,
  projects,
  disabled,
  onDataChange,
  onDelete,
  compactMode,
  showTimeEntryColumn,
  timeEntries,
  className,
}) => {
  const { id: meId } = useUserInfo();

  const availableProjects = projects
    .filter((project) =>
      isProjectAvailable(project, timeTracked.projectId || null),
    )
    .map(mapToOptions);

  const availableTimeEntries = [
    { value: '', label: '' },
    ...timeEntries
      .slice()
      .sort((a, b) => {
        const aTime = new Date(a.startDatetime).getTime();
        const bTime = new Date(b.startDatetime).getTime();
        return aTime - bTime;
      })
      .map((timeEntry) => ({
        value: timeEntry.id,
        label: formatTimeEntryLabel(timeEntry),
      })),
  ];

  const handleHoursChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        onDataChange({ ...timeTracked, hours: null });
        return;
      }
      const [hoursStr, minutesStr] = event.target.value.split(':');
      const totalMinutes =
        parseInt(hoursStr, 10) * 60 + parseInt(minutesStr, 10);
      onDataChange({
        ...timeTracked,
        hours: minToHoursInput(totalMinutes),
      });
    },
    [onDataChange, timeTracked],
  );

  const handleTimeEntryChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      onDataChange({
        ...timeTracked,
        timeEntryId: value && value !== '' ? value : null,
      });
    },
    [onDataChange, timeTracked],
  );

  const handleProjectChange = useCallback(
    (value: string | null) => {
      onDataChange({ ...timeTracked, projectId: value || null });
    },
    [onDataChange, timeTracked],
  );

  const renderProjectOption = useCallback(
    ({ option }: RenderOptionParams<OptionBase>) => {
      return <span>{option.label}</span>;
    },
    [],
  );

  const handleExtraFieldChange = useCallback(
    (
      extraFieldsInputs: ExtraFieldInternalValue[],
      recordTypeName: string | undefined,
    ) => {
      let updatedObject = {
        ...timeTracked,
        extraFields: extraFieldsInputs,
      };
      if (recordTypeName !== undefined) {
        updatedObject = {
          ...updatedObject,
          recordTypeName: recordTypeName,
        };
      }
      onDataChange(updatedObject);
    },
    [onDataChange, timeTracked],
  );

  return (
    <div className={className}>
      <Row>
        {showTimeEntryColumn && (
          <Col lg={2} xs={3}>
            <FormGroup>
              <StyledSelect
                disabled={disabled}
                value={timeTracked.timeEntryId || ''}
                onChange={handleTimeEntryChange}
                size={compactMode ? 'sm' : undefined}>
                {availableTimeEntries.map((timeEntry) => (
                  <option key={timeEntry.value} value={timeEntry.value}>
                    {timeEntry.label}
                  </option>
                ))}
              </StyledSelect>
            </FormGroup>
          </Col>
        )}
        <Col lg={showTimeEntryColumn ? 7 : 9} xs={showTimeEntryColumn ? 7 : 9}>
          <FormGroup>
            <SelectSingleBase
              isDisabled={disabled}
              value={timeTracked.projectId || null}
              onChange={handleProjectChange}
              options={availableProjects}
              renderOption={renderProjectOption}
              heightMode={compactMode ? 'small' : 'large'}
            />
          </FormGroup>
        </Col>
        <Col lg={2} xs={2}>
          <Form.Control
            value={
              timeTracked.hours === 0
                ? '00:00'
                : formatHoursToHHMM(timeTracked.hours)
            }
            type="time"
            required
            disabled={disabled}
            onChange={handleHoursChange}
            size={compactMode ? 'sm' : undefined}
          />
        </Col>
        {!disabled && (
          <Col lg={1} xs={1}>
            <Button
              variant="link"
              disabled={disabled}
              onClick={() => onDelete(timeTracked.id)}>
              <FlairIcon icon="trash-outline" color={Theme.color.gray1} />
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col lg={8} xs={12}>
          <ExtraFieldsAreaSimple
            objectApiName={ExtraFieldsSupportedObject.ProjectTimeEntry}
            formId={ExtraFieldsFormId.ProjectsTimeEntryEdit}
            onChange={handleExtraFieldChange}
            values={timeTracked.extraFields}
            recordTypeName={timeTracked.recordTypeName}
            hideEmptyLookups
            contextParams={[
              {
                name: extraFieldsContextParams.projectTimeEntryEdit.day,
                type: FieldType.Date,
                value: day,
              },
              {
                name: extraFieldsContextParams.projectTimeEntryEdit.project,
                type: FieldType.Reference,
                value: timeTracked.projectId ?? '',
              },
              {
                name: extraFieldsContextParams.projectTimeEntryEdit.employee,
                type: FieldType.Reference,
                value: meId,
              },
            ]}
            disabled={disabled}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ProjectDayTimeEntry;

function mapToOptions(project: ProjectWithStatus): OptionBase {
  return {
    value: project.id,
    label: project.name,
  };
}

function isProjectAvailable(
  project: ProjectWithStatus,
  selectedProjectId: string | null,
) {
  return selectedProjectId === project.id || project.status === 'ACTIVE';
}

function formatTimeEntryLabel(timeEntry: TimeEntryProps): string {
  const startTimeStr = formatTime24h(timeEntry.startDatetime);
  const endTimeStr = timeEntry.endDatetime
    ? formatTime24h(timeEntry.endDatetime)
    : '...';
  return `${startTimeStr} - ${endTimeStr}`;
}
